import { mutate } from './api'
import type { AnalysisDay, AnalysisPayload, DayLine } from './analysis-view'
import { dayLabelFull, dayLines, monthFull } from './analysis-view'
import { lineWorkDone } from './day-total-panel'
import { CLOSE_OUT_FROM } from './close-out-rules'
import { buzz } from './haptic'
import { landFlash, reducedMotion } from './motion'
import { paidLook, paidMoment } from './paid-moment'
import { lineDrawing } from './picture-tiles'
import { el, money, parseMoney, toast } from './ui'
import { currencySymbol } from './round-config'
import { getMe } from './me'
import { closeOutStillIn, mountCloseOutInto, updateCloseOutFigures } from './close-out'
import { getQueue } from './api'
import { ownersDecided, payeesOf } from './owner-pay-rules'
import './day-totals-team.css'

// Money's Day totals for a round with people to pay (Close out the day, 0095; Liam, 29 Oct 2026, design 2 v2:
// https://claude.ai/artifact/3zmLVcgda9fC5NmTx8keLe, "I like B", then months not weeks, a tick for paying people "easy to
// forget", and the phone). One row a day, the months folding (this one open), a bar per day (pay dark, after pay lemon),
// sorting by date, work or after pay, the month's best and lowest day, your average weekday, and a tick per day once
// you have paid your people. A day opens its close-out. A one-person round keeps the table it had.

export interface TeamDay {
  date: string
  work: number
  pay: number
  took: number // what the owners took that day (0097): their amounts on the close-out, and an owner's own pay from before
  tookWage: number // of which a wage (an accountant treats a wage and a share differently)
  notTaken: boolean // an owner's amount saved as not taken yet
  after: number
  cleans: number
  closed: boolean
  owe: number
  cashToCome: number
  toPay: number
  // Who was out that day (Liam, 30 Oct 2026: "a vital mention would be the worker(s) out that day ... after day"): work done
  // under their name or in a van, else, before Close out the day, the names on its day totals.
  out: string[]
  toPayWho: string[]
  unpaid: AnalysisDay[] // the day totals still to pay, ticked together
  paid: AnalysisDay[] // the day totals paid, unticked together
  paidAll: boolean
  payMissing: boolean
  // The workers' day totals (Liam, 4 Nov 2026, design A: "the column say worker amount"): each one's pay, tapped to change.
  workers: AnalysisDay[]
  // A day from before Close out the day: totals settled the old way (pay, no tick), read as paid until Paid is tapped.
  settled: AnalysisDay[] // someone worked and their pay was never put in // someone was paid and nobody is left to pay
}

const key = (s: string | null | undefined): string => (s ?? '').trim().toLowerCase()


// The day's figures from its lines (day totals and work with none) and its money. The owner's own pay and cash are
// never "to pay" or "to come": they are the owner's.
export function teamDays(data: AnalysisPayload, owners: readonly (string | null | undefined)[]): TeamDay[] {
  // Every owner who does jobs (Astra: Money read only the one signed in, so a second owner's pay and cash showed as owed).
  // A partner's day total from Close out the day carries no worker pay (0097), so it adds nothing; one from before keeps
  // the worker pay it had, as the month figures do, so adding a partner never rewrites old days (Astra's re-check).
  const own = new Set(owners.map(key).filter(Boolean))
  const ownersOnly = own
  const byDate = new Map<string, DayLine[]>()
  for (const line of dayLines(data, 'year').concat(olderLines(data))) {
    const list = byDate.get(line.date) ?? []
    list.push(line)
    byDate.set(line.date, list)
  }
  // A day with an owner's amount saved and nothing else left on it is still a day in Money (Astra's fourth re-check).
  const oldest = [...byDate.keys()].sort()[0] ?? data.today
  for (const t of data.owner_takes ?? []) if (t.period.length === 10 && t.period >= oldest && !byDate.has(t.period)) byDate.set(t.period, [])
  const out: TeamDay[] = []
  for (const [date, lines] of byDate) {
    let work = 0, pay = 0, cleans = 0, took = 0
    const unpaid: AnalysisDay[] = []
    const paid: AnalysisDay[] = []
    let payMissing = false
    const workers: AnalysisDay[] = []
    const settled: AnalysisDay[] = []
    for (const l of lines) {
      work += lineWorkDone(l)
      cleans += l.row.cleans
      if (l.kind !== 'total') continue
      const r = l.row
      // An owner's own pay from before, marked by hand (0097): what the owner took, never worker pay.
      if (r.owner_amount) { took += r.worker_pence; continue }
      if (own.has(key(r.worker))) continue
      pay += r.worker_pence
      if (r.worker_pence > 0 || r.paid_unknown) workers.push(r)
      // Before Close out the day a total with pay and no tick was settled the old way; ticked, or said not paid after all
      // (its Paid tapped, 0117), it reads as any day's (Liam, 4 Nov 2026: "i want to be able to click paid").
      if (date < CLOSE_OUT_FROM) {
        if (r.worker_pence > 0 && r.paid_at) paid.push(r)
        else if (r.worker_pence > 0 && r.pay_open_at) unpaid.push(r)
        else if (r.worker_pence > 0) settled.push(r)
        continue
      }
      // Pay never put in for someone who worked: the day is not closed out, never "All paid" (Astra).
      if (r.paid_unknown) payMissing = true
      if (r.worker_pence > 0 && !r.paid_at) unpaid.push(r)
      if (r.worker_pence > 0 && r.paid_at) paid.push(r)
    }
    const money = (data.day_money ?? []).filter((m) => m.date === date)
    // From Close out the day on, a day's work is its visits marked done: day totals hold each person's share of a van,
    // and a van half closed out would otherwise count its work once and a half, or half (Astra's re-check).
    // No visit done any more is no work (Astra: the old day total's work stayed).
    if (date >= CLOSE_OUT_FROM && data.day_money) work = money.reduce((n, m) => n + (m.work_pence ?? 0), 0)
    const takes = (data.owner_takes ?? []).filter((t) => t.period === date)
    took += takes.reduce((n, t) => n + t.amount_pence, 0)
    const tookWage = takes.filter((t) => t.category === 'wage').reduce((n, t) => n + t.amount_pence, 0)
    // From Close out the day on, a day is closed only once each owner's amount the setting asks for is in, as the
    // close-out page and the Diary decide (Astra's build review).
    // Who was out: work done that day under their name, or in a van; never a day total alone (Astra: visits moved away).
    const names = new Set([...money.map((m) => m.worker), ...(data.day_crews ?? []).filter((c) => c.date === date).flatMap((c) => [c.person, c.lead])].map((n) => n.trim()).filter(Boolean))
    const ownersIn = date < CLOSE_OUT_FROM || ownersDecided(payeesOf(data.owner_pay ? { id: null, version: 0, ...data.owner_pay } : null, data.pay_partners ?? [], takes as never, {
      people: [...names].map((n) => ({ worker: n, is_owner: ownersOnly.has(key(n)), out: true })), work, workerPay: pay }))
    out.push({
      date, work, pay, took, tookWage, notTaken: takes.some((t) => !t.paid_at), after: work - pay - took, cleans,
      out: names.size ? [...names] : [...new Set(lines.map((l) => (l.kind === 'total' ? l.row.worker ?? '' : l.row.worker).trim()).filter(Boolean))],
      // A rider in a van with no day total yet is not closed out (Astra's review).
      closed: lines.every((l) => l.kind !== 'missing') && !payMissing && ownersIn
        && !(data.day_crews ?? []).some((c) => c.date === date && !lines.some((l) => l.kind === 'total' && key(l.row.worker) === key(c.person))),
      owe: money.reduce((n, m) => n + m.owed_pence, 0),
      cashToCome: date < CLOSE_OUT_FROM ? 0 : money.filter((m) => !m.cash_in && !own.has(key(m.worker))).reduce((n, m) => n + m.cash_pence, 0),
      toPay: unpaid.reduce((n, r) => n + r.worker_pence, 0),
      toPayWho: unpaid.map((r) => (r.worker ?? '').trim() || 'someone'),
      unpaid,
      paid,
      paidAll: paid.length > 0 && unpaid.length === 0 && !payMissing,
      payMissing,
      workers,
      settled,
    })
  }
  return out.sort((a, b) => (a.date < b.date ? 1 : -1))
}

// dayLines' widest range is the year so far: the months before it (December back) come from the same rows.
function olderLines(data: AnalysisPayload): DayLine[] {
  const from = `${data.today.slice(0, 4)}-01-01`
  return [
    ...data.days.filter((r) => r.date < from).map((row) => ({ kind: 'total' as const, date: row.date, row })),
    ...(data.missing_days ?? []).filter((r) => r.date < from).map((row) => ({ kind: 'missing' as const, date: row.date, row })),
  ]
}

export function monthsOf(days: TeamDay[]): string[] {
  return [...new Set(days.map((d) => d.date.slice(0, 7)))].sort().reverse()
}

// The month's best and lowest day after pay, among days closed out (two or more of them).
export function bestAndLowest(days: TeamDay[]): { best: string | null; lowest: string | null } {
  const closed = days.filter((d) => d.closed)
  if (closed.length < 2) return { best: null, lowest: null }
  const best = closed.reduce((a, b) => (b.after > a.after ? b : a))
  const lowest = closed.reduce((a, b) => (b.after < a.after ? b : a))
  return { best: best.date, lowest: lowest === best ? null : lowest.date }
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const weekdayOf = (iso: string): number => (new Date(`${iso}T12:00:00Z`).getUTCDay() + 6) % 7
export function averageWeekdays(days: TeamDay[]): { day: string; pence: number }[] {
  return WEEKDAYS.map((day, i) => {
    const on = days.filter((d) => d.closed && weekdayOf(d.date) === i)
    return { day, pence: on.length ? Math.round(on.reduce((n, d) => n + d.after, 0) / on.length) : 0 }
  }).filter((a, i) => a.pence > 0 || i < 6)
}

// The accountant's download for a round with people to pay: one line a day, the same figures as the table (Astra: the
// old download kept each day total's own work done, which a van or a visit changed since makes different).
export function teamDaysCsv(data: AnalysisPayload, owners: readonly (string | null | undefined)[]): string {
  const p = (pence: number): string => (pence / 100).toFixed(2)
  // Worker pay and what the owners took are kept apart (0097): an accountant treats them differently. A month's wage is a
  // line of its own, on the month.
  const out = ['Date,Work done,Worker pay,Owners wages,Owners shares taken,After pay,Still to pay,Customers owe,Cash to come,Cleans,Closed out']
  for (const d of teamDays(data, owners).slice().reverse()) {
    out.push([d.date, p(d.work), p(d.pay), p(d.tookWage), p(d.took - d.tookWage), d.closed ? p(d.after) : '', p(d.toPay), p(d.owe), p(d.cashToCome), String(d.cleans), d.closed ? 'yes' : 'NO'].join(','))
  }
  // A name is quoted, so a comma or a line break in it never moves a column (Astra's re-check).
  const quoted = (v: string): string => `"${v.replace(/[\r\n]+/g, ' ').replace(/"/g, '""')}"`
  for (const t of (data.owner_takes ?? []).filter((x) => x.period.length === 7)) out.push([t.period, '', '', p(t.amount_pence), '', '', '', '', '', '', quoted(`${t.payee}'s wage for the month`)].join(','))
  return out.join('\n')
}

// ---- the page ------------------------------------------------------------------------------------------------------

type Sort = 'date' | 'work' | 'after'
let sort: Sort = 'date'
let openMonths: Set<string> | null = null
let flashToPay = false
let justPaid: string | null = null
// The one day opened in place (Liam, 30 Oct 2026, design B: "click into a tab and edit instead of close out"), kept
// across redraws of the table; closing it reads Money again so the row shows what was saved.
let openDay: string | null = null
// The open day's close-out itself, kept across Money reading itself again, so a box half typed in, a save under way and
// the place on the page all stay (Astra's build review: a refresh put £123.45 back to £100).
let openHost: { date: string; el: HTMLElement } | null = null

// lineButtons: the day's own "Change this day" for each day total on it (Money's day panel, design B, 24 Oct 2026), kept
// small at the end of the row for an older day, a note, or pay never entered.
export function teamDayTotals(data: AnalysisPayload, owners: readonly (string | null | undefined)[], redraw: () => void, refresh: () => void, csv: () => void, lineButtons: (date: string) => HTMLElement[] = () => []): HTMLElement {
  const days = teamDays(data, owners)
  const thisMonth = data.today.slice(0, 7)
  // A month with a wage on it, or this month on a monthly wage, shows even with no working days (Astra's third re-check).
  const wageMonths = (data.owner_takes ?? []).filter((t) => t.period.length === 7).map((t) => t.period)
  if (data.owner_pay?.mode === 'wage' && data.owner_pay.wage_kind === 'month') wageMonths.push(thisMonth)
  const months = [...new Set([...monthsOf(days), ...wageMonths])].sort().reverse()
  openMonths ??= new Set([thisMonth])
  const card = el('div', { class: 'team-days' })
  const mine = days.filter((d) => d.date.startsWith(thisMonth))
  const toPay = days.reduce((n, d) => n + d.toPay, 0)
  const who = [...new Set(days.flatMap((d) => d.toPayWho))]
  // Pay never put in is not "All paid" (Astra's re-check): the tile says how many days are missing it.
  const missingPay = days.filter((d) => d.payMissing).length
  // A day since Close out the day began that is not closed out may owe someone: never "All paid" while one is open.
  const openDays = days.filter((d) => !d.closed && !d.payMissing && d.date >= CLOSE_OUT_FROM && d.date <= data.today).length
  const sum = (list: TeamDay[], k: 'work' | 'after' | 'owe' | 'cashToCome'): number => list.reduce((n, d) => n + (d.closed || k === 'work' ? d[k] : 0), 0)

  // The tiles: this month's work and what is left after pay; still to pay your people (every month: it is what gets
  // forgotten); customers still owe and cash to come in.
  const unsure = missingPay + openDays
  const payTile = el('div', { class: `team-days-tile${toPay || unsure ? ' is-due' : ' is-clear'}` },
    el('span', {}, 'Still to pay your people'), el('strong', {}, toPay ? money(toPay) : missingPay ? 'Pay not put in' : openDays ? 'Not closed out' : 'All paid'),
    el('small', {}, [toPay ? who.join(', ') : '', missingPay ? `${missingPay} day${missingPay === 1 ? '' : 's'} with pay not put in` : '', openDays ? `${openDays} day${openDays === 1 ? '' : 's'} not closed out` : ''].filter(Boolean).join(' · ') || 'Nobody waiting'))
  // What the owners took (0097): the days' amounts and a month's wage. "After pay" is after both.
  const wageOf = (m: string): number => (data.owner_takes ?? []).filter((t) => t.period === m).reduce((n, t) => n + t.amount_pence, 0)
  const tookThisMonth = mine.reduce((n, d) => n + d.took, 0) + wageOf(thisMonth)
  const anyTook = tookThisMonth > 0 || (data.owner_pay?.mode ?? 'keep') !== 'keep'
  const tiles = el('div', { class: 'team-days-tiles' },
    el('div', { class: 'team-days-tile' }, el('span', {}, `${monthFull(thisMonth)} work done`), el('strong', {}, money(sum(mine, 'work')))),
    ...(anyTook ? [el('div', { class: 'team-days-tile' }, el('span', {}, `${monthFull(thisMonth)} owners took`), el('strong', {}, money(tookThisMonth)),
      el('small', {}, mine.some((d) => d.notTaken) ? 'some not taken yet' : 'after worker pay'))] : []),
    el('div', { class: 'team-days-tile is-keep' }, el('span', {}, `${monthFull(thisMonth)} ${anyTook ? 'after pay' : 'after worker pay'}`), el('strong', {}, money(sum(mine, 'after') - wageOf(thisMonth))), el('small', {}, 'before fuel and kit')),
    payTile,
    el('div', { class: 'team-days-tile is-due' }, el('span', {}, 'Customers still owe'), el('strong', {}, money(mine.reduce((n, d) => n + d.owe, 0))),
      el('small', {}, `${money(days.reduce((n, d) => n + d.cashToCome, 0))} cash to hand in`)))
  const avg = averageWeekdays(mine)
  const top = Math.max(1, ...avg.map((a) => a.pence))
  const chart = el('div', { class: 'team-days-avg' }, el('strong', {}, `Your average day in ${monthFull(thisMonth)}, after worker pay`))
  for (const a of avg) {
    const bar = el('span', { class: `team-days-avg-bar${a.pence === top ? ' is-top' : ''}` })
    bar.style.width = `${Math.round((a.pence / top) * 100)}%`
    chart.append(el('span', { class: 'team-days-avg-row' }, el('strong', {}, a.day), el('span', { class: 'team-days-avg-track' }, bar), el('strong', {}, money(a.pence))))
  }
  card.append(el('div', { class: 'team-days-top' }, tiles, chart))

  const bar = el('div', { class: 'team-days-tools' })
  for (const [s, words] of [['date', 'By date'], ['work', 'Biggest work'], ['after', 'Best after pay']] as [Sort, string][]) {
    const b = el('button', { type: 'button', class: `team-days-sort${sort === s ? ' is-on' : ''}`, 'aria-pressed': sort === s ? 'true' : 'false' }, words)
    b.addEventListener('click', () => { sort = s; redraw() })
    bar.append(b)
  }
  const dl = el('button', { type: 'button', class: 'team-days-csv' }, 'Download for the accountant')
  dl.addEventListener('click', csv)
  bar.append(dl)
  card.append(bar)

  const table = el('div', { class: 'team-days-table', role: 'table', 'aria-label': 'Day totals' })
  table.append(el('div', { class: 'team-days-head', role: 'row' }, ...['Day', 'Out', 'Jobs', 'Work: pay and after pay', 'Worker pay', 'After pay', 'Customers owe', 'Cash to come', 'Paid your people?', 'Edit'].map((t) => el('span', { role: 'columnheader' }, t))))
  const widest = Math.max(1, ...days.map((d) => d.work))
  const thisYear = Number(data.today.slice(0, 4))
  for (const m of months) {
    const list = days.filter((d) => d.date.startsWith(m))
    const isOpen = openMonths.has(m)
    const left = list.reduce((n, d) => n + d.toPay, 0)
    const head = el('button', { type: 'button', class: 'team-days-month', 'aria-expanded': isOpen ? 'true' : 'false' },
      el('span', { class: 'team-days-month-name' }, el('span', { 'aria-hidden': 'true' }, isOpen ? '▾' : '▸'), `${monthFull(m)}${m.slice(0, 4) === String(thisYear) ? '' : ` ${m.slice(0, 4)}`}`),
      el('span', {}, ''),
      el('span', {}, (() => { const n = list.reduce((k, d) => k + d.cleans, 0); return `${n} job${n === 1 ? '' : 's'}` })()),
      el('span', { class: 'is-num' }, money(sum(list, 'work'))),
      el('span', { class: 'is-num' }, list.some((d) => d.pay) ? money(list.reduce((n, d) => n + d.pay, 0)) : ''),
      el('span', { class: 'is-num' }, money(sum(list, 'after') - wageOf(m))),
      el('span', { class: 'is-due' }, list.some((d) => d.owe) ? money(list.reduce((n, d) => n + d.owe, 0)) : ''),
      el('span', { class: 'is-due' }, list.some((d) => d.cashToCome) ? money(list.reduce((n, d) => n + d.cashToCome, 0)) : ''),
      (() => {
        // "All paid" only when every day since Close out the day began is closed out with its pay in.
        const open = list.some((d) => (d.payMissing || !d.closed) && d.date >= CLOSE_OUT_FROM && d.date <= data.today)
        return el('span', { class: left || open ? 'is-due' : 'is-good' }, left ? `${money(left)} to pay` : open ? 'Not all closed out' : list.some((d) => d.paidAll) ? 'All paid ✓' : '')
      })(),
      el('span', {}, ''))
    head.addEventListener('click', () => { if (isOpen) openMonths!.delete(m); else openMonths!.add(m); redraw() })
    table.append(head)
    // A monthly wage (0097): taken off once, on the month. Marked taken here; tapping it again takes it off.
    const wage = data.owner_pay?.mode === 'wage' && data.owner_pay.wage_kind === 'month' ? data.owner_pay.wage_pence : null
    const wageRows = (data.owner_takes ?? []).filter((t) => t.period === m)
    // One line for each owner who does jobs, each their own wage (Astra: the first owner's name was used for everyone).
    if (isOpen && (wage || wageRows.length) && m <= thisMonth) {
      const who = [...new Set([...owners.map((o) => (o ?? '').trim()).filter(Boolean), ...wageRows.map((r) => r.payee)])]
      for (const name of who.length ? who : ['Owner']) table.append(wageLine(m, wage, wageRows.filter((r) => key(r.payee) === key(name)), name, refresh))
    }
    if (!isOpen) continue
    const { best, lowest } = bestAndLowest(list)
    const ordered = sort === 'date' ? list : [...list].sort((a, b) => (sort === 'work' ? b.work - a.work : b.after - a.after))
    for (const d of ordered) {
      // Another day opens at once; the one closed is read again behind it, so its row shows what was saved.
      const toggle = (): void => {
        const was = openDay
        if (was === d.date) { openDay = null; openHost = null; refresh(); return }
        openDay = d.date
        redraw()
        if (was) refresh()
        // Bring the opened day up under the top bar, so its tiles are in sight (Liam, 3 Nov 2026: "it should be
        // further down so you can see the tiles").
        requestAnimationFrame(() => {
          const opened = document.querySelector<HTMLElement>('.team-days-row.is-opened')
          if (!opened) return
          const top = (document.querySelector<HTMLElement>('.topbar')?.getBoundingClientRect().bottom ?? 0) + 8
          const y = window.scrollY + opened.getBoundingClientRect().top - top
          window.scrollTo({ top: Math.max(0, y), behavior: reducedMotion() ? 'auto' : 'smooth' })
        })
      }
      table.append(dayRow(d, widest, thisYear, sort === 'date' ? (d.date === best ? 'best' : d.date === lowest ? 'lowest' : null) : null, refresh, toggle, new Set(owners.map(key).filter(Boolean)), (data.owner_takes ?? []).filter((t) => t.period === d.date), redraw))
      if (openDay === d.date) {
        // The day's close-out in place, then its saved day totals as they are (a total with no visits behind it any more
        // stays reachable; Astra's drawing check).
        const figures = { owe: d.owe, work: d.work, jobs: d.cleans, kept: d.closed ? d.after : null }
        let host = openHost?.date === d.date && closeOutStillIn(openHost.el) ? openHost.el : null
        if (!host) {
          host = el('div', { class: 'team-days-open', role: 'region', 'aria-label': `${dayLabelFull(d.date, thisYear)}, opened` })
          openHost = { date: d.date, el: host }
          mountCloseOutInto(d.date, host, { ...figures, saved: refresh })
        } else updateCloseOutFigures(figures)
        table.append(host)
        host.querySelector('.team-days-saved')?.remove()
        const saved = lineButtons(d.date)
        if (saved.length) host.append(el('div', { class: 'team-days-saved' }, el('span', {}, 'Saved day totals'), ...saved))
      }
    }
  }
  card.append(table)
  card.append(el('p', { class: 'team-days-note' }, 'Tap the circle once you have paid your people. A month opens and folds from its heading; tap a day to open it here and change it.'))
  // A day is its visits marked done, so there is no blank line to add. Work done without Roundly is logged on the
  // customer, and its day then shows here (Liam, 31 Oct 2026: "unless i've not used the app to record work").
  const log = el('button', { type: 'button', class: 'link-button' }, 'Log completed work')
  log.addEventListener('click', () => void import('./booking').then(({ openBooking }) => openBooking({ completed: true, onCompleted: refresh })))
  card.append(el('p', { class: 'team-days-log' }, el('span', {}, 'Did work Roundly doesn\'t know about?'), log))

  if (flashToPay) { flashToPay = false; landFlash(payTile) }
  justPaid = null
  return card
}

function dayRow(d: TeamDay, widest: number, thisYear: number, tag: 'best' | 'lowest' | null, reload: () => void, toggle: () => void, own: Set<string> = new Set(), takes: NonNullable<AnalysisPayload['owner_takes']> = [], redraw: () => void = reload): HTMLElement {
  const me = (getMe()?.workerName ?? '').trim().toLowerCase()
  const label = dayLabelFull(d.date, thisYear)
  const opened = openDay === d.date
  const row = el('div', { class: `team-days-row${d.closed ? '' : ' is-open'}${justPaid === d.date ? ' is-just-paid' : ''}${opened ? ' is-opened' : ''}`, role: 'row' })
  const link = el('button', { type: 'button', class: 'team-days-day', 'aria-expanded': opened ? 'true' : 'false' }, label)
  link.addEventListener('click', toggle)
  const fill = el('span', { class: `team-days-fill${d.closed ? '' : ' is-dashed'}` })
  fill.style.width = `${Math.round((d.work / widest) * 100)}%`
  if (d.closed && d.work > 0) {
    const pay = el('span', { class: 'team-days-fill-pay' }); pay.style.flex = String(Math.max(0, d.pay))
    const took = el('span', { class: 'team-days-fill-took' }); took.style.flex = String(Math.max(0, d.took))
    const after = el('span', { class: 'team-days-fill-after' }); after.style.flex = String(Math.max(0, d.after))
    fill.append(pay, took, after)
  }
  const bits: string[] = []
  if (d.payMissing) bits.push('Pay not put in')
  else if (!d.closed) bits.push('Not closed out')
  else {
    if (d.took) bits.push(`owners took ${money(d.took)}${d.notTaken ? ', not taken yet' : ''}`)
    if (d.toPay) bits.push(`pay ${d.toPayWho.join(' and ')} ${money(d.toPay)}`)
    if (d.cashToCome) bits.push(`${money(d.cashToCome)} cash to come`)
    if (d.owe) bits.push(`owed ${money(d.owe)}`)
  }
  const barCell = el('span', { class: 'team-days-barcell' },
    el('span', { class: 'team-days-track' }, fill), el('strong', { class: 'team-days-work' }, money(d.work)),
    el('span', { class: `team-days-sub${bits.length ? ' is-due' : ''}` }, bits.join(' · ')))
  const paid = el('span', { class: 'team-days-paid' })
  if (!d.closed) paid.append(el('span', { class: 'team-days-close' }, 'Not closed out'))
  else if (d.toPay) {
    const b = el('button', { type: 'button', class: 'team-days-tick', 'aria-label': `Paid ${d.toPayWho.join(' and ')} ${money(d.toPay)} for ${label}` },
      el('span', { class: 'team-days-ring', 'aria-hidden': 'true' }), el('span', { class: 'team-days-tick-words' }, `Pay ${d.toPayWho.join(' and ')} ${money(d.toPay)}`))
    b.addEventListener('click', () => void markPaid(d, row, b, reload))
    paid.append(b)
  } else if (d.paidAll) {
    // Paid: tapping it again takes the tick off (a slip), the same day totals together.
    const done = el('button', { type: 'button', class: `team-days-done${justPaid === d.date && !reducedMotion() ? ' is-fresh' : ''}`, 'aria-label': `All paid for ${label}. Tap to take the tick off` },
      el('span', { class: 'team-days-ring is-paid', 'aria-hidden': 'true' }, lineDrawing('tick', 14)), 'All paid')
    done.addEventListener('click', () => void markUnpaid(d, done, reload))
    paid.append(done)
  } else if (d.settled.length) {
    // A day from before Close out the day with pay on it: settled the old way, as its close-out says (Liam, 31 Oct
    // 2026: the drawing's column was blank on a closed day; 3 Nov: "it should be a visible tick"). Liam, 4 Nov 2026,
    // design A: "i want to be able to click paid": a tap says it was not paid after all, and it reads Pay again.
    const done = el('button', { type: 'button', class: 'team-days-done is-settled', title: 'Settled before Close out the day', 'aria-label': `Paid for ${label}. Tap to take the tick off` },
      el('span', { class: 'team-days-ring is-paid', 'aria-hidden': 'true' }, lineDrawing('tick', 14)), 'Paid')
    done.addEventListener('click', () => void markUnpaid(d, done, reload))
    paid.append(done)
  } else if (d.closed && d.out.length && d.out.every((n) => own.has(key(n)) || n.toLowerCase() === me)) {
    // Only owners out: nobody to pay (design B). Liam, 4 Nov 2026, design A: "i want to be able to click paid me also":
    // your own pay is a tick too, filled in with what the day made after pay, changed in the Worker pay column.
    const mine = takes.find((t) => key(t.payee) === me)
    if (me && (mine || !takes.length)) {
      const b = el('button', { type: 'button', class: mine?.paid_at ? 'team-days-done' : 'team-days-tick', 'aria-label': mine?.paid_at ? `You took ${money(mine.amount_pence)} for ${label}. Tap to take the tick off` : `Tick: you have paid yourself for ${label}` },
        el('span', { class: `team-days-ring${mine?.paid_at ? ' is-paid' : ''}`, 'aria-hidden': 'true' }, ...(mine?.paid_at ? [lineDrawing('tick', 14)] : [])),
        el('span', { class: 'team-days-tick-words' }, mine?.paid_at ? 'Paid me' : 'Tick: paid me'))
      b.addEventListener('click', (e) => {
        e.stopPropagation()
        if (mine) void markTaken(mine, !mine.paid_at, b, row, reload, 'You')
        else { payEditing = { date: d.date, who: ME }; redraw() }
      })
      paid.append(b)
    } else {
      const open = takes.filter((t) => !t.paid_at)
      paid.append(takes.length
        ? el('span', { class: `team-days-nobody${open.length ? ' is-due' : ' is-good'}` }, open.length ? `${money(open.reduce((n, t) => n + t.amount_pence, 0))} not taken yet` : 'All taken')
        : el('span', { class: 'team-days-nobody' }, d.out.length === 1 ? 'Just you · nobody to pay' : 'Owners only · nobody to pay'))
    }
  }
  // Who was out, after the day (you as You): tapping opens the day, where who rode in the van is changed.
  const outNames = d.out.map((n) => (n.toLowerCase() === me ? 'You' : n.split(/\s+/)[0] ?? n))
  // Design B (Liam, 3 Nov 2026: "i want this on the main bit mostly so i can see it at a glance", then "I like B"): each
  // person out is a face with a dot, green once paid, a red ring while still to pay; an owner keeps theirs, so no dot.
  // Owners paid from the work (Your business, How do you get paid: a share, a wage or asked each day; Liam, 3 Nov 2026:
  // "even its me it should be interactive ... we pay ourselves based of work done"; off, the usual "keep what is left",
  // they have nothing to take and no dot): their amount taken or not yet, tapped like anyone's.
  const takeOf = (n: string) => takes.find((t) => key(t.payee) === key(n) || (n.toLowerCase() === me && key(t.payee) === 'you'))
  const status = (n: string): 'paid' | 'due' | null => {
    if (own.has(key(n)) || n.toLowerCase() === me) { const t = takeOf(n); return t ? (t.paid_at ? 'paid' : 'due') : null }
    if (d.unpaid.some((r) => key(r.worker) === key(n))) return 'due'
    if (d.paid.some((r) => key(r.worker) === key(n)) || d.settled.some((r) => key(r.worker) === key(n))) return 'paid'
    return null
  }
  const faces = d.out.map((n) => {
    const shown = n.toLowerCase() === me ? 'You' : n.split(/\s+/)[0] ?? n, st = status(n)
    // The app's one tick on paid (the same drawn tick as All paid), a red ring while still to pay.
    const dot = st ? [el('span', { class: `team-days-face-dot is-${st}`, 'aria-hidden': 'true' }, ...(st === 'paid' ? [lineDrawing('tick', 9)] : []))] : []
    if (!st) return el('span', { class: 'team-days-face', title: shown }, shown)
    // A tap marks them paid (or takes it off again): a worker as the day's Pay does, an owner their own amount.
    const owner = own.has(key(n)) || n.toLowerCase() === me
    const b = el('button', { type: 'button', class: 'team-days-face is-tap', title: `${shown}: ${st === 'paid' ? 'paid. Tap to take the tick off' : 'still to pay. Tap once paid'}`,
      'aria-label': `${shown} ${st === 'paid' ? 'paid' : 'still to pay'} for ${label}. ${st === 'paid' ? 'Tap to take the tick off' : 'Tap once paid'}` }, shown, ...dot) as HTMLButtonElement
    b.addEventListener('click', (e) => {
      e.stopPropagation()
      if (owner) { const t = takeOf(n); if (t) void markTaken(t, st !== 'paid', b, row, reload, shown); return }
      paid.querySelector<HTMLButtonElement>('.team-days-tick, .team-days-done')?.click()
    })
    return b
  })
  const said = d.out.map((n) => { const shown = n.toLowerCase() === me ? 'You' : n.split(/\s+/)[0] ?? n, st = status(n); return `${shown}${st === 'paid' ? ' paid' : st === 'due' ? ' still to pay' : ''}` })
  // A group, not a button, so each face can be its own button; a click between them opens the day (the row's click).
  const who = el('span', { class: 'team-days-who', role: 'group', 'aria-label': outNames.length ? `Out on ${label}: ${said.join(', ')}` : `Nobody out on ${label}` }, ...(faces.length ? faces : ['Nobody']))
  link.append(el('span', { class: 'team-days-who-phone' }, outNames.join(', ')))
  const editingHere = payEditing?.date === d.date
  const workerPay = workerPayCell(d, me, takes, redraw)
  const hide = (cell: HTMLElement): HTMLElement => { if (editingHere) cell.classList.add('team-days-under-edit'); return cell }
  row.append(link, who,
    el('span', { class: 'team-days-jobs' }, String(d.cleans)),
    barCell,
    hide(workerPay),
    hide(el('strong', { class: 'team-days-after' }, d.closed ? money(d.after) : '')),
    hide(el('span', { class: 'is-due' }, d.owe ? money(d.owe) : '')),
    hide(el('span', { class: 'is-due' }, d.cashToCome ? money(d.cashToCome) : '')),
    hide(paid),
    el('span', { class: 'team-days-tag' }, tag ? el('span', { class: `is-${tag}` }, tag === 'best' ? 'Best day' : 'Lowest') : '', editButton(label, opened, toggle)))
  if (editingHere) { row.classList.add('is-editing'); row.insertBefore(payEditor(d, me, takes, row, reload, redraw), row.lastElementChild) }
  // A click anywhere on the row opens or folds the day (Liam, 3 Nov 2026: "can click anywhere on that tile to collapse
  // it"); its own buttons (Paid, the tick) keep their jobs.
  row.addEventListener('click', (e) => { if (!(e.target as Element | null)?.closest('button, a, input, select, .team-days-payedit')) toggle() })
  return row
}

// ---- Worker pay (Liam, 4 Nov 2026, design A: https://claude.ai/artifact/6Xxs6NYgEh8XqQ7SVLhaz2) ----------------------
// "worker column i should be able to one tap edit paid and the column say worker amount": each worker's pay for the day,
// one tap to change it right there on the line (Cash or Bank saves it paid, Not paid yet saves it with no tick); a day
// with only you shows what you took, tapped the same way.

const ME = '\u0000me'
let payEditing: { date: string; who: string } | null = null

function workerPayCell(d: TeamDay, me: string, takes: NonNullable<AnalysisPayload['owner_takes']>, redraw: () => void): HTMLElement {
  const cell = el('span', { class: 'team-days-workerpay' })
  const open = (who: string) => (e: Event): void => { e.stopPropagation(); payEditing = { date: d.date, who }; redraw() }
  for (const r of d.workers) {
    const name = (r.worker ?? '').trim() || 'Someone'
    const shown = name.split(/\s+/)[0] ?? name
    const b = el('button', { type: 'button', class: 'team-days-payamt', 'aria-label': r.paid_unknown ? `Put in ${shown}'s pay for ${d.date}` : `${shown}'s pay ${money(r.worker_pence)}. Tap to change it` },
      r.paid_unknown ? 'Put in pay' : money(r.worker_pence), el('small', {}, shown))
    b.addEventListener('click', open(key(name)))
    cell.append(b)
  }
  if (!d.workers.length) {
    const mine = takes.find((t) => key(t.payee) === me)
    if (mine) {
      const b = el('button', { type: 'button', class: 'team-days-payamt', 'aria-label': `You took ${money(mine.amount_pence)}. Tap to change it` }, money(mine.amount_pence), el('small', {}, 'You took'))
      b.addEventListener('click', open(ME))
      cell.append(b)
    } else if (d.closed) cell.append(el('span', { class: 'team-days-payamt-none' }, 'Nobody to pay'))
  }
  return cell
}

function payEditor(d: TeamDay, me: string, takes: NonNullable<AnalysisPayload['owner_takes']>, row: HTMLElement, reload: () => void, redraw: () => void): HTMLElement {
  const who = payEditing!.who
  const r = who === ME ? null : d.workers.find((x) => key(x.worker) === who) ?? null
  const mine = takes.find((t) => key(t.payee) === me) ?? null
  const shown = r ? ((r.worker ?? '').trim().split(/\s+/)[0] || 'Them') : 'You took'
  const start = r ? (r.paid_unknown ? null : r.worker_pence) : (mine?.amount_pence ?? Math.max(0, d.after))
  const box = el('div', { class: 'team-days-payedit', role: 'group', 'aria-label': r ? `Change ${shown}'s pay for ${d.date}` : `What you took for ${d.date}` })
  const input = el('input', { class: 'team-days-payedit-input', inputmode: 'decimal', value: start !== null ? (start / 100).toFixed(2) : '', placeholder: '0.00', 'aria-label': r ? `${shown}'s pay` : 'What you took' }) as HTMLInputElement
  const afterLine = el('strong', { class: 'team-days-payedit-after' })
  const was = r ? r.worker_pence : (mine?.amount_pence ?? 0)
  const showAfter = (): void => {
    let pence: number | null = null
    try { pence = parseMoney(input.value) } catch { pence = null }
    afterLine.textContent = d.closed && pence !== null ? `After pay ${money(d.after + was - pence)}` : ''
  }
  input.addEventListener('input', showAfter)
  showAfter()
  const close = (): void => { payEditing = null; document.removeEventListener('pointerdown', outside, true); redraw() }
  const outside = (e: Event): void => { if (!box.contains(e.target as Node)) close() }
  input.addEventListener('keydown', (e) => { if (e.key === 'Escape') close() })
  const go = (way: 'cash' | 'bank' | null, button: HTMLButtonElement): void => {
    let pence: number | null
    try { pence = parseMoney(input.value) } catch (err) { toast((err as Error).message); return }
    if (pence === null) { toast('Type the amount first'); input.focus(); return }
    document.removeEventListener('pointerdown', outside, true)
    for (const b of box.querySelectorAll('button')) (b as HTMLButtonElement).disabled = true
    const undo = way ? paidMoment({ host: row, from: button, way, mark: null }) : () => {}
    const old = d.date < CLOSE_OUT_FROM
    const paid = way ? { paid_at: true, paid_how: way } : { paid_at: false, paid_how: null, ...(old ? { pay_open_at: true } : {}) }
    const save = r
      ? mutate({ label: `${shown}'s pay ${money(pence)} for ${d.date}`, method: 'PATCH', path: `/api/day-ledger/${r.id}`, body: { version: r.version, worker_cut_pence: pence, ...paid } })
      : mine
        ? mutate({ label: `You took ${money(pence)} for ${d.date}`, method: 'PATCH', path: `/api/owner-takes/${mine.id}`, body: { version: mine.version, amount_pence: pence, paid: !!way, ...(way ? { paid_how: way } : {}) } })
        : mutate({ label: `You took ${money(pence)} for ${d.date}`, method: 'POST', path: '/api/owner-takes', body: { period: d.date, payee: getMe()?.workerName?.trim() ?? 'Owner', category: 'took', amount_pence: pence, paid: !!way, ...(way ? { paid_how: way } : {}) } })
    void save.then((ack) => {
      payEditing = null
      if (!ack.ok && ack.status !== 0) { undo(); toast(ack.error ?? 'Did not save. Try again.') }
      else { buzz('light'); if (way) { justPaid = d.date; flashToPay = true } }
      window.setTimeout(reload, way && !reducedMotion() ? 320 : 0)
    })
  }
  const label = el('label', { class: 'team-days-payedit-label' }, el('span', {}, `${shown} ${currencySymbol()}`), input)
  const cash = el('button', { type: 'button', class: 'team-days-payedit-way' }, 'Cash') as HTMLButtonElement
  const bank = el('button', { type: 'button', class: 'team-days-payedit-way' }, 'Bank') as HTMLButtonElement
  const later = el('button', { type: 'button', class: 'team-days-payedit-later' }, r ? 'Not paid yet' : 'Not taken yet') as HTMLButtonElement
  paidLook(cash, 'cash'); paidLook(bank, 'bank')
  cash.addEventListener('click', () => go('cash', cash))
  bank.addEventListener('click', () => go('bank', bank))
  later.addEventListener('click', () => go(null, later))
  box.append(label, cash, bank, later, afterLine)
  // Anywhere else leaves it as it was; the box is put in sight and its amount ready to type over.
  window.setTimeout(() => { document.addEventListener('pointerdown', outside, true); input.focus(); input.select() }, 0)
  return box
}

// The Edit column's one button a day: opens the day in place, or folds it away (the drawn pencil; an up arrow when open,
// Liam, 3 Nov 2026: "the x makes it seem like it wont save ... it should be an arrow to indicate collapsible"; each
// change has already saved as it was made).
function editButton(label: string, opened: boolean, toggle: () => void): HTMLElement {
  const b = el('button', { type: 'button', class: `team-days-edit${opened ? ' is-opened' : ''}`, 'aria-label': opened ? `Fold ${label} away` : `Edit ${label}`, 'aria-expanded': opened ? 'true' : 'false' },
    lineDrawing(opened ? 'chevronUp' : 'pencil', 18))
  b.addEventListener('click', toggle)
  return b
}

// Every day total still to pay that day is marked paid together. The paid moment plays from the tap; the page is drawn
// again once it has spread, with the Still to pay tile glowing once.
async function markPaid(d: TeamDay, row: HTMLElement, button: HTMLButtonElement, reload: () => void): Promise<void> {
  button.disabled = true
  const undo = paidMoment({ host: row, from: button, way: 'bank', mark: button.querySelector<HTMLElement>('.team-days-ring') })
  let refused = false
  for (const r of d.unpaid) {
    if (r.id == null) continue
    const ack = await mutate({ label: `Paid ${r.worker ?? ''} for ${d.date}`, method: 'PATCH', path: `/api/day-ledger/${r.id}`, body: { version: r.version, paid_at: true } })
    if (!ack.ok && ack.status !== 0) { refused = true; toast(ack.error ?? 'Did not save. Try again.') }
  }
  if (refused) { undo(); reload(); return }
  buzz('light')
  justPaid = d.date
  flashToPay = true
  window.setTimeout(reload, reducedMotion() ? 0 : 320)
}

// An owner's amount for the day marked taken, or the tick taken off (PATCH /api/owner-takes/:id, paid true/false).
async function markTaken(t: NonNullable<AnalysisPayload['owner_takes']>[number], taken: boolean, button: HTMLButtonElement, row: HTMLElement, reload: () => void, who: string): Promise<void> {
  button.disabled = true
  const undo = taken ? paidMoment({ host: row, from: button, way: 'bank', mark: button.querySelector<HTMLElement>('.team-days-face-dot') }) : () => {}
  const ack = await mutate({ label: `${who} ${taken ? 'took' : 'not taken'} ${money(t.amount_pence)}`, method: 'PATCH', path: `/api/owner-takes/${t.id}`, body: { version: t.version, paid: taken } })
  if (!ack.ok && ack.status !== 0) { undo(); toast(ack.error ?? 'Did not save. Try again.') }
  else if (!taken) buzz('light')
  reload()
}

async function markUnpaid(d: TeamDay, button: HTMLButtonElement, reload: () => void): Promise<void> {
  button.disabled = true
  // A day from before Close out the day stays to pay once its tick is off, never settled the old way again (0117).
  const old = d.date < CLOSE_OUT_FROM
  for (const r of [...d.paid, ...d.settled]) {
    if (r.id == null) continue
    const ack = await mutate({ label: `Not paid ${r.worker ?? ''} for ${d.date}`, method: 'PATCH', path: `/api/day-ledger/${r.id}`, body: { version: r.version, paid_at: false, paid_how: null, ...(old ? { pay_open_at: true } : {}) } })
    if (!ack.ok && ack.status !== 0) toast(ack.error ?? 'Did not save. Try again.')
  }
  flashToPay = true
  reload()
}

// A month's wage on the month (0097): "Wage £2,000 · Mark taken", or taken, with its Undo.
function wageLine(month: string, wage: number | null, rows: NonNullable<AnalysisPayload['owner_takes']>, owner: string, reload: () => void): HTMLElement {
  const line = el('div', { class: 'team-days-wage', role: 'row' })
  // Saved on this phone, waiting for signal: nothing more until it has gone (Astra: a second tap made a second).
  if (getQueue().some((q) => q.path.startsWith('/api/owner-takes') && (((q.body as { period?: string; payee?: string } | undefined)?.period === month && key((q.body as { payee?: string }).payee) === key(owner)) || rows.some((r) => q.path === `/api/owner-takes/${r.id}`)))) {
    line.append(el('span', {}, `${owner}'s wage for ${monthFull(month)}: saved on this phone. It sends when there is signal.`))
    return line
  }
  const row = rows[0]
  if (row) {
    line.append(el('span', {}, `${row.payee}'s wage for ${monthFull(month)}: ${money(row.amount_pence)}${row.paid_at ? ' taken' : ''}`))
    const undo = el('button', { type: 'button', class: 'team-days-sort' }, 'Take it off')
    undo.addEventListener('click', () => { undo.setAttribute('disabled', 'disabled'); void mutate({ label: `Wage off ${month}`, method: 'DELETE', path: `/api/owner-takes/${row.id}`, body: { version: row.version } }).then(reload) })
    line.append(undo)
    return line
  }
  line.append(el('span', {}, `${owner}'s wage for ${monthFull(month)}: ${money(wage ?? 0)}`))
  const take = el('button', { type: 'button', class: 'team-days-sort' }, 'Mark it taken')
  take.addEventListener('click', () => {
    take.setAttribute('disabled', 'disabled')
    buzz('light')
    void mutate({ label: `Wage ${month}`, method: 'POST', path: '/api/owner-takes', body: { period: month, payee: owner || 'Owner', category: 'wage', amount_pence: wage ?? 0, rule: `wage month ${wage ?? 0}`, paid: true, paid_how: 'bank' } })
      .then((ack) => { if (!ack.ok && ack.status !== 0) toast(ack.error ?? 'Did not save. Try again.'); reload() })
  })
  line.append(take)
  return line
}
